import * as React from 'react'
import {
  decodeTaggedProps,
  DeltaDecodeError,
  type CompareResult,
  type TaggedNode,
  type TaggedRel
} from '@axilon-apps/domains-ontology-ui-state'
import {
  CollapsedSection,
  CountChips,
  DIFF_TABLE_PAGE_SIZE,
  DiffPagerRow,
  DiffRow,
  DiffTable,
  KindBadge,
  PropertyDiffTable,
  type CountChip,
  type PropertyDiffRow
} from '../DiffTable'
import {
  changedPropertyRows,
  labelsChangedRow,
  labelsListingRow,
  propertyListingRows,
  type ChangedPropertyRows
} from './entity-diff-rows'
import { entityDisplayName, labelsDiverge, relFullId, relLabel } from './entity-display'

type RowTone = 'add' | 'change' | 'remove'
const KIND_TEXT: Record<RowTone, string> = { add: 'added', change: 'modified', remove: 'deleted' }
const KIND_ORDER: Record<RowTone, number> = { add: 0, change: 1, remove: 2 }

/**
 * Renders what a merge would bring in, in the shared diff-table language the
 * ingest diff established: count chips, one flat scannable
 * `Kind | Type | Entity` row per change, and click-to-expand property detail
 * — before → after for modifications (differing fields first, the untouched
 * remainder folded away), full listings for additions and removals, and a
 * labels row whenever labels are part of the change. Tagged props decode
 * once per entity; a decode failure surfaces as that row's error, never a
 * crash.
 */
export function ComparePreview({
  result: raw,
  excludeIds,
  emptyMessage = 'Nothing to merge — this branch brings no changes.'
}: {
  result: CompareResult
  /** Logical ids to drop from the preview (e.g. the conflicting entities, shown
   * in their own section) — counts are recomputed so the badges stay honest. */
  excludeIds?: ReadonlySet<string>
  emptyMessage?: string
}) {
  const result = React.useMemo(
    () => (excludeIds && excludeIds.size ? excludeCompareIds(raw, excludeIds) : raw),
    [raw, excludeIds]
  )
  const rows = React.useMemo(() => buildRows(result), [result])
  const postgresRows = React.useMemo(
    () => [
      ...(result.postgres?.added ?? []).map((change) => ({ change, tone: 'add' as const })),
      ...(result.postgres?.changed ?? []).map((change) => ({ change, tone: 'change' as const })),
      ...(result.postgres?.removed ?? []).map((change) => ({ change, tone: 'remove' as const }))
    ],
    [result]
  )
  // One open entity row across the entire preview; keys are logical ids,
  // disjoint across kinds and change groups.
  const [openKey, setOpenKey] = React.useState<string | null>(null)
  const [page, setPage] = React.useState(0)
  const [postgresPage, setPostgresPage] = React.useState(0)
  // A new compare result or exclusion set starts from the first page.
  React.useEffect(() => setPage(0), [rows])
  React.useEffect(() => setPostgresPage(0), [postgresRows])

  const { counts } = result
  const graphTotal =
    counts.nodesAdded +
    counts.nodesChanged +
    counts.nodesRemoved +
    counts.relsAdded +
    counts.relsChanged +
    counts.relsRemoved
  const postgresCounts = result.postgresCounts
  const postgresTotal = postgresCounts
    ? postgresCounts.added + postgresCounts.changed + postgresCounts.removed
    : 0

  if (graphTotal + postgresTotal === 0 && !result.postgresHistoryUnavailable) {
    return <p className="text-xs text-ink-3">{emptyMessage}</p>
  }

  const chips: CountChip[] = []
  const added = counts.nodesAdded + counts.relsAdded
  const changed = counts.nodesChanged + counts.relsChanged
  const removed = counts.nodesRemoved + counts.relsRemoved
  if (added > 0) chips.push({ text: `${added.toLocaleString()} added`, tone: 'add' })
  if (changed > 0) chips.push({ text: `${changed.toLocaleString()} modified`, tone: 'change' })
  if (removed > 0) chips.push({ text: `${removed.toLocaleString()} deleted`, tone: 'remove' })

  // safePage tolerates a set that shrank (a changed exclusion set) between
  // the last page click and this render.
  const pageCount = Math.max(1, Math.ceil(rows.length / DIFF_TABLE_PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const shown = rows.slice(safePage * DIFF_TABLE_PAGE_SIZE, (safePage + 1) * DIFF_TABLE_PAGE_SIZE)
  const postgresPageCount = Math.max(1, Math.ceil(postgresRows.length / DIFF_TABLE_PAGE_SIZE))
  const safePostgresPage = Math.min(postgresPage, postgresPageCount - 1)
  const shownPostgres = postgresRows.slice(
    safePostgresPage * DIFF_TABLE_PAGE_SIZE,
    (safePostgresPage + 1) * DIFF_TABLE_PAGE_SIZE
  )
  const postgresChips: CountChip[] = []
  if (postgresCounts?.added)
    postgresChips.push({ text: `${postgresCounts.added} added`, tone: 'add' })
  if (postgresCounts?.changed)
    postgresChips.push({ text: `${postgresCounts.changed} modified`, tone: 'change' })
  if (postgresCounts?.removed)
    postgresChips.push({ text: `${postgresCounts.removed} deleted`, tone: 'remove' })

  return (
    <div className="flex flex-col gap-2">
      {result.postgresHistoryUnavailable ? (
        <p className="text-xs text-ink-3" role="status">
          Postgres history is unavailable for this branch’s merge base. Graph changes are shown
          below.
        </p>
      ) : null}
      {graphTotal > 0 ? <CountChips chips={chips} /> : null}
      {graphTotal > 0 && result.truncated ? (
        <p className="text-xs text-ink-3">
          The change set is too large to display — merging applies all of it.
        </p>
      ) : graphTotal > 0 ? (
        <DiffTable kindHeader="Change" entityKindHeader="Kind">
          {shown.map((row) =>
            row.error ? (
              <DiffRow
                key={row.key}
                badge={<KindBadge tone={row.tone}>{KIND_TEXT[row.tone]}</KindBadge>}
                type={row.type}
                entityKind={row.kind}
                entity={
                  <span role="alert" className="text-st-down">
                    {row.name} — {row.error}
                  </span>
                }
                entityTitle={row.urn}
              />
            ) : (
              <DiffRow
                key={row.key}
                badge={<KindBadge tone={row.tone}>{KIND_TEXT[row.tone]}</KindBadge>}
                type={row.type}
                entityKind={row.kind}
                entity={
                  row.tone === 'remove' ? (
                    <span className="text-ink-3 line-through">{row.name}</span>
                  ) : (
                    row.name
                  )
                }
                entityTitle={row.urn}
                summary={rowSummary(row)}
                expandable={row.body != null}
                open={openKey === row.key}
                onOpenChange={(open) => setOpenKey(open ? row.key : null)}
              >
                {row.body ? <RowBody body={row.body} urn={row.urn} /> : undefined}
              </DiffRow>
            )
          )}
          <DiffPagerRow page={safePage} total={rows.length} onPageChange={setPage} />
        </DiffTable>
      ) : null}
      {postgresTotal > 0 ? (
        <section className="flex flex-col gap-2" aria-label="Postgres configuration changes">
          <h3 className="text-xs font-medium text-ink-2">Postgres configuration</h3>
          <CountChips chips={postgresChips} />
          {result.postgresTruncated ? (
            <p className="text-xs text-ink-3">
              The Postgres change set is too large to display. Counts include all changes.
            </p>
          ) : (
            <DiffTable kindHeader="Change" typeHeader="Table" entityHeader="Row identity">
              {shownPostgres.map(({ change, tone }) => (
                <DiffRow
                  key={`${change.table}:${JSON.stringify(change.key)}:${tone}`}
                  badge={<KindBadge tone={tone}>{KIND_TEXT[tone]}</KindBadge>}
                  type={change.table}
                  entity={JSON.stringify(change.key)}
                  entityTitle={JSON.stringify(change.key)}
                />
              ))}
              <DiffPagerRow
                page={safePostgresPage}
                total={postgresRows.length}
                onPageChange={setPostgresPage}
              />
            </DiffTable>
          )}
        </section>
      ) : null}
    </div>
  )
}

/** The expansion content of one row: differing/unchanged split for changes,
 * a full (possibly struck) listing for adds/removes, labels included. */
interface BodyModel {
  labelsRow?: PropertyDiffRow
  changed?: ChangedPropertyRows
  listing?: PropertyDiffRow[]
  struck?: boolean
}

interface RowModel {
  key: string
  tone: RowTone
  /** Node or relationship — the mini-tag beside the type column. */
  kind: 'node' | 'rel'
  type: string
  name: string
  urn: string
  /** Differing-field count (changes) or total field count (adds/removes). */
  fieldCount: number
  body?: BodyModel
  /** Decode failure — the row renders as an alert instead of content. */
  error?: string
}

function rowSummary(row: RowModel): string | undefined {
  if (row.fieldCount <= 0) return undefined
  return `(${row.fieldCount} field${row.fieldCount !== 1 ? 's' : ''})`
}

function RowBody({ body, urn }: { body: BodyModel; urn: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="px-2 font-mono text-10 break-all text-ink-3">{urn}</p>
      {body.changed ? (
        <>
          <PropertyDiffTable
            rows={[...(body.labelsRow ? [body.labelsRow] : []), ...body.changed.differing]}
          />
          {body.changed.unchanged.length > 0 ? (
            <CollapsedSection
              title={`${body.changed.unchanged.length} unchanged propert${
                body.changed.unchanged.length !== 1 ? 'ies' : 'y'
              }`}
            >
              <div className="px-2 py-1.5">
                <PropertyDiffTable columns="value" rows={body.changed.unchanged} />
              </div>
            </CollapsedSection>
          ) : null}
        </>
      ) : (
        <PropertyDiffTable
          columns="value"
          struck={body.struck}
          rows={[...(body.labelsRow ? [body.labelsRow] : []), ...(body.listing ?? [])]}
        />
      )}
    </div>
  )
}

function decodeErrorText(err: unknown): string {
  return err instanceof DeltaDecodeError ? err.message : 'could not decode'
}

function buildRows(result: CompareResult): RowModel[] {
  const out: RowModel[] = [
    ...(result.nodes?.added ?? []).map((n) => nodeListingRow(n, 'add')),
    ...(result.nodes?.removed ?? []).map((n) => nodeListingRow(n, 'remove')),
    ...(result.nodes?.changed ?? []).map(nodeChangedRow),
    ...(result.relationships?.added ?? []).map((r) => relListingRow(r, 'add')),
    ...(result.relationships?.removed ?? []).map((r) => relListingRow(r, 'remove')),
    ...(result.relationships?.changed ?? []).map(relChangedRow)
  ]
  return out.sort(
    (a, b) =>
      KIND_ORDER[a.tone] - KIND_ORDER[b.tone] ||
      a.type.localeCompare(b.type) ||
      a.name.localeCompare(b.name)
  )
}

function nodeListingRow(node: TaggedNode, tone: 'add' | 'remove'): RowModel {
  const base = {
    key: node.id,
    tone,
    kind: 'node' as const,
    type: node.labels?.[0] ?? 'Node',
    name: entityDisplayName(node),
    urn: node.id
  }
  try {
    const listing = propertyListingRows(decodeTaggedProps(node.properties ?? {}))
    const labelsRow =
      node.labels && node.labels.length > 0 ? labelsListingRow(node.labels) : undefined
    return {
      ...base,
      fieldCount: listing.length,
      body: { labelsRow, listing, struck: tone === 'remove' }
    }
  } catch (err) {
    return { ...base, fieldCount: 0, error: decodeErrorText(err) }
  }
}

function relListingRow(rel: TaggedRel, tone: 'add' | 'remove'): RowModel {
  const base = {
    key: rel.id,
    tone,
    kind: 'rel' as const,
    type: rel.type,
    name: relLabel(rel),
    urn: relFullId(rel)
  }
  try {
    const listing = propertyListingRows(decodeTaggedProps(rel.properties ?? {}))
    return { ...base, fieldCount: listing.length, body: { listing, struck: tone === 'remove' } }
  } catch (err) {
    return { ...base, fieldCount: 0, error: decodeErrorText(err) }
  }
}

function nodeChangedRow(ch: { before: TaggedNode; after: TaggedNode }): RowModel {
  const base = {
    key: ch.after.id,
    tone: 'change' as const,
    kind: 'node' as const,
    type: ch.after.labels?.[0] ?? 'Node',
    name: entityDisplayName(ch.after),
    urn: ch.after.id
  }
  try {
    const changed = changedPropertyRows(
      decodeTaggedProps(ch.before.properties ?? {}),
      decodeTaggedProps(ch.after.properties ?? {})
    )
    const labelsChanged = labelsDiverge(ch.before.labels, ch.after.labels)
    const labelsRow = labelsChanged
      ? labelsChangedRow(ch.before.labels ?? [], ch.after.labels ?? [])
      : undefined
    return {
      ...base,
      fieldCount: changed.differing.length + (labelsChanged ? 1 : 0),
      body: { labelsRow, changed }
    }
  } catch (err) {
    return { ...base, fieldCount: 0, error: decodeErrorText(err) }
  }
}

function relChangedRow(ch: { before: TaggedRel; after: TaggedRel }): RowModel {
  const base = {
    key: ch.after.id,
    tone: 'change' as const,
    kind: 'rel' as const,
    type: ch.after.type,
    name: relLabel(ch.after),
    urn: relFullId(ch.after)
  }
  try {
    const changed = changedPropertyRows(
      decodeTaggedProps(ch.before.properties ?? {}),
      decodeTaggedProps(ch.after.properties ?? {})
    )
    return { ...base, fieldCount: changed.differing.length, body: { changed } }
  } catch (err) {
    return { ...base, fieldCount: 0, error: decodeErrorText(err) }
  }
}

/**
 * Drops entities whose logical id is in `exclude` and recomputes the counts so
 * a filtered preview's badges stay honest. A truncated result carries no
 * entity arrays to filter, so it passes through unchanged.
 */
function excludeCompareIds(r: CompareResult, exclude: ReadonlySet<string>): CompareResult {
  if (r.truncated) return r
  const keep = <T extends { id: string }>(xs?: T[]): T[] =>
    (xs ?? []).filter((x) => !exclude.has(x.id))
  const keepChanged = <T extends { id: string }>(xs?: { before: T; after: T }[]) =>
    (xs ?? []).filter((c) => !exclude.has(c.after.id))
  const nodes = {
    added: keep(r.nodes?.added),
    removed: keep(r.nodes?.removed),
    changed: keepChanged(r.nodes?.changed)
  }
  const relationships = {
    added: keep(r.relationships?.added),
    removed: keep(r.relationships?.removed),
    changed: keepChanged(r.relationships?.changed)
  }
  return {
    ...r,
    nodes,
    relationships,
    counts: {
      nodesAdded: nodes.added.length,
      nodesChanged: nodes.changed.length,
      nodesRemoved: nodes.removed.length,
      relsAdded: relationships.added.length,
      relsChanged: relationships.changed.length,
      relsRemoved: relationships.removed.length
    },
    truncated: r.truncated
  }
}
